import { inject, Injectable } from '@angular/core';
import { DateTime as LuxonDateTime } from 'luxon';

import { DemoDataService, DEMO_ADDRESSES_KEY } from 'src/shared/services/demo-data.service';
import { DateSettings, ListingCalendarService } from 'src/shared/services/listing-calendar.service';

import { Util } from 'src/shared/util/util';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { OwnerBlock, Period } from 'src/models/schedule-weekly-view.model';

/** A contiguous run of owner-blocked nights, in real (unclamped) dates. */
export interface OwnerBlockRange {
  addressId: number;
  startDate: string;  // first blocked night (YYYY-MM-DD)
  endDate: string;    // last blocked night, inclusive (YYYY-MM-DD)
  nights: number;
  reason: string | null;  // the note left when the nights were blocked
}

/**
 * Owner blocks are stored as per-date `is_blocked` rows in the Listing
 * Builder date-settings store (`address-settings-by-date`) — the same rows
 * the optimizer pricing calendar writes for single dates. This service reads
 * those rows and coalesces consecutive blocked dates into ranges, and writes
 * ranges back (`true` blocks, `null` clears), so the schedule can treat a run
 * of blocked nights as one "owner block". Each date also carries the reason it
 * was blocked, so back-to-back blocks with different reasons stay separate.
 */
@Injectable({ providedIn: 'root' })
export class OwnerBlocksService {

  private readonly listingCalendar = inject(ListingCalendarService);
  private readonly demoData = inject(DemoDataService);
  private readonly util = inject(Util);
  private readonly parseDateTimePipe = new ParseDateTimePipe();

  // How far to widen the query when a blocked run reaches a window edge, and
  // how many times — together they reach ~2 years either side of the request.
  private static readonly EDGE_EXPANSION_DAYS = 180;
  private static readonly MAX_EDGE_EXPANSIONS = 4;

  private useDummyData = true;

  /**
   * Sample blocks for the demo/preview worlds, expressed as offsets from today
   * so they never go stale. The first one is clamped to the current week so the
   * schedule always opens with a block on screen (see `demoRanges`). Both are
   * assigned to addresses WITHOUT demo inquiries so a block never stacks on an
   * inquiry card.
   */
  private static readonly DEMO_BLOCK_TEMPLATES = [
    { addressIndex: 0, startOffsetDays: 2, nights: 4, clampToCurrentWeek: true, reason: 'Owner staying' },
    { addressIndex: 1, startOffsetDays: 14, nights: 3, clampToCurrentWeek: false, reason: null },
  ];

  // The demo account has no `is_blocked` rows of its own, so the schedule would
  // show an empty blocks lane. Real accounts must never see fabricated blocks.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  /**
   * Blocked-night ranges for one property covering [dateFrom, dateTo].
   *
   * A run that reaches a window edge usually continues past it, so we widen and
   * refetch until both ends are anchored by an unblocked night. Without this the
   * returned range is clipped to whatever window the caller happened to ask for,
   * and callers treat it as the real range — `toScheduleBlock` puts it in
   * `period`, which is what the card label and the `unblockRange` write use.
   */
  async getBlocksForAddress(addressId: number, dateFrom: string, dateTo: string): Promise<OwnerBlockRange[]> {
    if (this.showsDummyData) {
      return this.demoRanges([addressId], dateFrom, dateTo);
    }
    let from = dateFrom;
    let to = dateTo;
    let blockedRows = this.blockedRowsIn(await this.listingCalendar.getDateSettings(addressId, from, to));

    for (let i = 0; i < OwnerBlocksService.MAX_EDGE_EXPANSIONS && blockedRows.length; i++) {
      const touchesStart = blockedRows[0].date <= from;
      const touchesEnd = blockedRows[blockedRows.length - 1].date >= to;
      if (!touchesStart && !touchesEnd) { break; }
      if (touchesStart) { from = this.shiftDays(from, -OwnerBlocksService.EDGE_EXPANSION_DAYS); }
      if (touchesEnd) { to = this.shiftDays(to, OwnerBlocksService.EDGE_EXPANSION_DAYS); }
      const widened = this.blockedRowsIn(await this.listingCalendar.getDateSettings(addressId, from, to));
      // Nothing new turned up at the edge, so the run really does end there.
      if (widened.length === blockedRows.length) { break; }
      blockedRows = widened;
    }

    return this.coalesce(addressId, blockedRows);
  }

  /** Blocked-night ranges across properties; failures for one address never sink the rest. */
  async getBlocksForAddresses(addressIds: number[], dateFrom: string, dateTo: string): Promise<OwnerBlockRange[]> {
    // Short-circuits before the per-address loop so the samples are spread
    // across the list rather than repeated on every property.
    if (this.showsDummyData) {
      return this.demoRanges(addressIds, dateFrom, dateTo);
    }
    const CONCURRENCY = 8;
    const ranges: OwnerBlockRange[] = [];
    for (let i = 0; i < addressIds.length; i += CONCURRENCY) {
      const chunk = addressIds.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(
        chunk.map((addressId) => this.getBlocksForAddress(addressId, dateFrom, dateTo))
      );
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          ranges.push(...result.value);
        }
      });
    }
    return ranges;
  }

  /**
   * Blocks every night in [startDate, endDate] with the given reason. The
   * reason is always sent, so an empty one removes any reason already stored
   * on those nights.
   */
  blockRange(addressId: number, startDate: string, endDate: string, reason = ''): Promise<void> {
    return this.listingCalendar.setDateSettings(addressId, startDate, endDate, {
      isBlocked: true,
      blockReason: reason,
    });
  }

  /**
   * Edits a block: blocks [startDate, endDate] with the new reason, then
   * unblocks the nights of the previous range that fall outside it. Blocking
   * first means a failed write never leaves the owner's nights bookable.
   */
  async updateRange(
    addressId: number,
    previous: Period,
    startDate: string,
    endDate: string,
    reason = ''
  ): Promise<void> {
    await this.blockRange(addressId, startDate, endDate, reason);
    const released: Period[] = [];
    if (previous.startDate < startDate) {
      const dayBeforeStart = this.shiftDays(startDate, -1);
      released.push({
        startDate: previous.startDate,
        endDate: previous.endDate < dayBeforeStart ? previous.endDate : dayBeforeStart,
      });
    }
    if (previous.endDate > endDate) {
      const dayAfterEnd = this.shiftDays(endDate, 1);
      released.push({
        startDate: previous.startDate > dayAfterEnd ? previous.startDate : dayAfterEnd,
        endDate: previous.endDate,
      });
    }
    for (const range of released) {
      await this.unblockRange(addressId, range.startDate, range.endDate);
    }
  }

  /** Clears the stored block on every night in [startDate, endDate]. */
  unblockRange(addressId: number, startDate: string, endDate: string): Promise<void> {
    return this.listingCalendar.setDateSettings(addressId, startDate, endDate, { isBlocked: null });
  }

  /**
   * Shapes a range for the schedule views, mirroring
   * `Schedule.parseCalendarReservations`: start/end clamped to the visible
   * period with weekday spans, `period` keeping the real dates.
   */
  toScheduleBlock(range: OwnerBlockRange, selectedPeriodRange: Period, daysOfWeek: string[], propertyName?: string): OwnerBlock {
    const isStartInsidePeriod = this.util.isDateInsidePeriod(range.startDate, selectedPeriodRange);
    const isEndInsidePeriod = this.util.isDateInsidePeriod(range.endDate, selectedPeriodRange);
    return {
      id: `${range.addressId}:${range.startDate}:${range.endDate}`,
      addressId: range.addressId,
      title: `${this.parseDateTimePipe.transform(range.startDate, 'MMM d')} - ${this.parseDateTimePipe.transform(range.endDate, 'MMM d')}`,
      startWeekday: isStartInsidePeriod ? this.util.getWeekdayFromDay(range.startDate) : daysOfWeek[0],
      startDate: isStartInsidePeriod ? range.startDate : selectedPeriodRange.startDate,
      endWeekday: isEndInsidePeriod ? this.util.getWeekdayFromDay(range.endDate) : daysOfWeek[6],
      endDate: isEndInsidePeriod ? range.endDate : selectedPeriodRange.endDate,
      period: {
        startDate: range.startDate,
        endDate: range.endDate,
      },
      nights: range.nights,
      propertyName,
      reason: range.reason,
    };
  }

  /**
   * Sample blocks for a demo/preview session, anchored to today and assigned
   * to the caller's addresses in order (falling back to the first property
   * when the demo has fewer addresses than templates). Filtered to the
   * requested window so paging the schedule away from today empties out the
   * same way a real calendar would.
   */
  private demoRanges(addressIds: number[], dateFrom: string, dateTo: string): OwnerBlockRange[] {
    const ids = (addressIds ?? []).filter((id) => id > 0);
    if (ids.length === 0) { return []; }
    // The demo inquiries live on the first three demo addresses (see
    // constants/demo/inquiries.json), so blocks pick from the remaining
    // addresses — a sample block must never stack on an inquiry card, even
    // when the schedule is filtered down to only inquiry addresses.
    const inquiryAddressIds = new Set(this.demoInquiryAddressIds());
    const pool = ids.filter((id) => !inquiryAddressIds.has(id));
    if (pool.length === 0) { return []; }
    const today = LuxonDateTime.local().startOf('day');
    // Sunday of the current Mon-Sun week: the last day both of the schedule's
    // week views (today→+6, and Mon-Sun) are guaranteed to be showing.
    const lastDayOfCurrentWeek = today.endOf('week').startOf('day');
    return OwnerBlocksService.DEMO_BLOCK_TEMPLATES
      .map((template) => {
        const requested = today.plus({ days: template.startOffsetDays });
        const start = template.clampToCurrentWeek
          ? LuxonDateTime.min(requested, lastDayOfCurrentWeek)
          : requested;
        return this.buildRange(
          pool[template.addressIndex] ?? pool[0],
          start.toISODate(),
          start.plus({ days: template.nights - 1 }).toISODate(),
          template.reason
        );
      })
      .filter((range) => range.endDate >= dateFrom && range.startDate <= dateTo);
  }

  // Ids of the demo addresses that carry the mock inquiries: inquiries.json
  // assigns them to ADDRESS_ID_1..3, which resolve to the first three demo
  // addresses captured at login.
  private demoInquiryAddressIds(): number[] {
    try {
      const addresses = JSON.parse(localStorage.getItem(DEMO_ADDRESSES_KEY) || '[]');
      return (Array.isArray(addresses) ? addresses : [])
        .slice(0, 3)
        .map((address) => address?.id)
        .filter((id) => !!id);
    } catch (_e) {
      return [];
    }
  }

  // A run continues only across consecutive nights with the same reason, so two
  // back-to-back blocks left for different reasons read as two blocks.
  private coalesce(addressId: number, sortedRows: DateSettings[]): OwnerBlockRange[] {
    const ranges: OwnerBlockRange[] = [];
    let runStart: string | null = null;
    let runEnd: string | null = null;
    let runReason: string | null = null;
    sortedRows.forEach(({ date, blockReason }) => {
      const reason = blockReason ?? null;
      const isConsecutive = runEnd !== null &&
        LuxonDateTime.fromISO(date).diff(LuxonDateTime.fromISO(runEnd), 'days').days === 1;
      if (runStart === null) {
        runStart = date;
        runEnd = date;
        runReason = reason;
      } else if (isConsecutive && reason === runReason) {
        runEnd = date;
      } else {
        ranges.push(this.buildRange(addressId, runStart, runEnd, runReason));
        runStart = date;
        runEnd = date;
        runReason = reason;
      }
    });
    if (runStart !== null) {
      ranges.push(this.buildRange(addressId, runStart, runEnd, runReason));
    }
    return ranges;
  }

  private blockedRowsIn(rows: DateSettings[]): DateSettings[] {
    return rows
      .filter((row) => row.isBlocked)
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  private shiftDays(date: string, days: number): string {
    return LuxonDateTime.fromISO(date).plus({ days }).toISODate();
  }

  private buildRange(addressId: number, startDate: string, endDate: string, reason: string | null = null): OwnerBlockRange {
    const nights = Math.round(
      LuxonDateTime.fromISO(endDate).diff(LuxonDateTime.fromISO(startDate), 'days').days
    ) + 1;
    return { addressId, startDate, endDate, nights, reason };
  }

}
